import React, { useEffect } from 'react'
import { Controller, FormProvider, SubmitHandler } from 'react-hook-form'
import styled from 'styled-components'
import { Panel } from 'primereact/panel'
import { Button } from 'primereact/button'
import { Tag } from 'primereact/tag'

import {
    CHARGEPOINT_MIN_CURRENT,
    capiPhaseModeOptions,
    singlePhaseOptions,
} from '../../common/domain/electrical-settings'
import useDefer from '../../common/hooks/useDefer'
import { useChargePointContext } from '../../common/context/ChargePointContext'
import { notifyError, notifySuccess } from '../../common/features/notificationsManager/Notify'
import useCapiFormValidation, {
    ICapiFormInput,
    capiChargerDefaultValues,
} from '../../common/hooks/useCapiFormValidation'
import { ChargePoint, Connector, useChargePointUpdateMutation } from '../../common/redux/api/web-api'
import { SFormRow } from '../../common/styles/Form'
import { mapCapiChargepoint } from '../../common/utils/objectMappers'
import Text from '../../ui/Text'
import { useAdaptConnectorNaming } from '../adaptConnectorNaming'
import { LoadbalancerGroup } from '../createLoadbalancerGroup'
import { isPartnerDomain, isSupportDomain } from '../currentDomain'
import { getLoadbalancerGroup } from '../get-loadbalancer-group'
import { InfoIcon } from '../icons/info-icon'
import { Input } from '../../common/uikit/Input'
import { Dropdown } from '../../common/uikit/Dropdown'
import { confirm } from '../../common/uikit/ConfirmDialog'
import { isChargingConnector } from '../findChargingConnector'
import connectorTypesList from '../connectorTypesList'
import useText from '../../common/hooks/useText'

export const ErrorMsg = styled.div`
    display: block;
    height: 34px;
    position: relative;
    top: 15px;
    margin-top: -15px;
    padding: 2px 0;
    color: ${({ theme }) => theme.font.errorColor};

    &.default-color {
        color: inherit;
    }

    &.comment {
        font-weight: var(--font-weight-light);
    }
`

const SLabel = styled.label`
    display: flex;
    gap: 4px;
    align-items: center;
    justify-content: space-between;
`

const SConnectorTitle = styled(SLabel)`
    font-size: 14px;
    font-weight: var(--font-weight-light);
`

const SConnector = styled.div`
    flex: 1;
`

export type CapiCurrentSettingsProps = {
    isRestrictedAccess: boolean
    chargePoint: ChargePoint
}

export function CapiCurrentSettings({ chargePoint, isRestrictedAccess }: CapiCurrentSettingsProps): React.ReactElement {
    const text = useText()
    const { value: isLoadbalancerGroupActive } = useDefer(() => {
        if (!chargePoint.loadBalancerGroupId) return false
        return getLoadbalancerGroup(chargePoint.loadBalancerGroupId).then((group: LoadbalancerGroup) => group.active)
    })
    const { autoRefresh, setAutoRefresh } = useChargePointContext()
    const [updateChargePoint, { isLoading: chargePointUpdating }] = useChargePointUpdateMutation()

    const adaptConnectorNaming = useAdaptConnectorNaming()

    /* Include in shared schema for CAPI */

    const capiFormValidationMethods = useCapiFormValidation(chargePoint)
    const {
        handleSubmit,
        register,
        control,
        formState: { errors, isDirty, isValid },
        reset,
        trigger,
        watch,
    } = capiFormValidationMethods

    useEffect(() => {
        reset(capiChargerDefaultValues(chargePoint), {
            keepDirtyValues: true,
            keepDirty: true,
            keepErrors: true,
            keepIsValid: true,
        })
    }, [chargePoint, reset])

    useEffect(() => {
        trigger()
    }, [trigger])

    /* Include in shared schema for CAPI */
    useEffect(() => {
        const subscription = watch(() => trigger())
        return () => subscription.unsubscribe()
    }, [trigger, watch])

    const saveChargePoint: SubmitHandler<ICapiFormInput> = (formData) => {
        try {
            updateChargePoint({
                chargePoint: mapCapiChargepoint(chargePoint, formData),
            }).unwrap()
            notifySuccess(text('msgChargerUpdated'))
        } catch (error) {
            notifyError(error)
        }
    }

    const getInfo = (connector: Connector) =>
        text('msgConnectorInfo', {
            name: adaptConnectorNaming(connector.name),
            factoryCurrent: connector.factoryCurrent,
            supplyCurrent: chargePoint.supplyCurrent,
            connectorFunctionType: connector.connectorFunctionType,
            userCurrent: connector.userCurrent || 0,
            installationCurrent: connector.installationCurrent,
            installationPhaseMode: connector.installationPhaseMode,
            installationPhase: connector.installationPhase,
            loadBalancerOfflineCurrent: connector.loadBalancerOfflineCurrent,
            loadBalancerOfflinePhase: connector.loadBalancerOfflinePhase,
        })

    const connectorTypesOptions = connectorTypesList.map((type) => ({
        label: type,
        value: type,
    }))

    const shouldDisabledInputs = (isSupportDomain || isPartnerDomain) && autoRefresh

    const toggleSettingsLock = async () => {
        if (!autoRefresh) {
            setAutoRefresh(true)
            return
        }

        const confirmed = await confirm({
            header: text('lblUnlockingInstallationSettings'),
            message: text('msgUnlockingInstallationSettings'),
        })

        if (confirmed) {
            setAutoRefresh(false)
        }
    }

    return (
        <div data-testid="details">
            <FormProvider {...capiFormValidationMethods}>
                <div className="flex justify-content-between mb-2">
                    <div className="flex gap-2">
                        <Button
                            onClick={() => void toggleSettingsLock()}
                            className="flex gap-2"
                            label={autoRefresh ? text('lblUnlock') : text('lblUnlockedSettings')}
                            icon={
                                autoRefresh ? <i className="pi pi-lock"></i> : <i className="pi pi-lock-open ml-1"></i>
                            }
                        ></Button>
                    </div>
                    <div>
                        {(isSupportDomain || !chargePoint?.ocppEnabled) && (
                            <Button
                                label={text('btnSave')}
                                disabled={
                                    chargePointUpdating || (isSupportDomain && autoRefresh) || !isDirty || !isValid
                                }
                                onClick={handleSubmit(saveChargePoint)}
                                data-testid="save-btn"
                            />
                        )}
                    </div>
                </div>

                {shouldDisabledInputs && (
                    <Tag
                        severity="warning"
                        data-testid="charger-status-alert"
                        value={text('msgInstallationRequiresUnlockCommand')}
                        className="text-base mb-2"
                        icon={<i className="pi pi-info-circle mr-1"></i>}
                    ></Tag>
                )}
                <div>
                    {chargePoint.inActiveLoadBalancerGroup && (
                        <Tag
                            data-testid="fields-disabled"
                            value={text('msgSomeFieldsAreDisabled')}
                            severity="warning"
                            className="text-base mb-2"
                            icon={<i className="pi pi-info-circle mr-1"></i>}
                        ></Tag>
                    )}
                </div>
                <div className="grid w-full">
                    <div className="lg:col-6 col-12">
                        <Panel
                            toggleable
                            header={
                                <span className="mt-2 flex">
                                    <i className="pi pi-wrench"></i>
                                    <span className="ml-2"> {text('lblPhysicalInstallationSettings')}</span>
                                </span>
                            }
                            data-testid="physical-installation-settings-widget"
                        >
                            <div className="flex ml-3 mb-2">
                                <div className="col-10">
                                    <div className="flex justify-content-between">
                                        <Text code="lblSupplyCurrentField" />

                                        <span>
                                            <InfoIcon title={text('msgSupplyCurrentField')} size={20} />
                                        </span>
                                    </div>

                                    <Input
                                        {...register('supplyCurrent')}
                                        type="number"
                                        min={CHARGEPOINT_MIN_CURRENT}
                                        id="supplyCurrent"
                                        disabled={isRestrictedAccess || autoRefresh}
                                        data-testid={'supplyCurrent-input'}
                                        required
                                        error={errors.supplyCurrent?.message}
                                    />
                                </div>
                            </div>
                        </Panel>
                        {chargePoint.loadBalancerGroupId && (
                            <Panel
                                toggleable
                                className="mt-3"
                                header={
                                    <span className="mt-2 flex">
                                        <span className="fa fa-gears mr-2 text-500"></span>
                                        <span className="ml-2"> {text('lblLoadbalancerSettings')}</span>
                                    </span>
                                }
                            >
                                {chargePoint.connectors?.filter(isChargingConnector)?.map((connector) => (
                                    <React.Fragment key={`lb-settings-${connector.connectorId}`}>
                                        <div className="flex ml-3 mb-2">
                                            <div className="col-10">
                                                <div className="flex justify-content-between">
                                                    <Text code="lblLoadBalancerOfflineCurrentLimitField" />

                                                    <span>
                                                        <InfoIcon
                                                            title={text('msgLoadBalancerOfflineCurrentLimitField')}
                                                            size={20}
                                                        />
                                                    </span>
                                                </div>
                                                <Input
                                                    type="number"
                                                    min={CHARGEPOINT_MIN_CURRENT}
                                                    id={`lbc_${connector.connectorId}`}
                                                    disabled={
                                                        chargePoint.inActiveLoadBalancerGroup ||
                                                        isRestrictedAccess ||
                                                        autoRefresh ||
                                                        isLoadbalancerGroupActive
                                                    }
                                                    data-testid={'lbOfflineCurrent-input-' + connector.connectorId}
                                                    {...register(`lbc_${connector.connectorId}`)}
                                                    error={errors[`lbc_${connector.connectorId}`]?.message}
                                                />
                                            </div>
                                        </div>

                                        <div className="flex ml-3 mb-2">
                                            <div className="col-10">
                                                <div className="flex justify-content-between">
                                                    <Text code="lblLoadBalancerOfflinePhase" />

                                                    <span>
                                                        <InfoIcon
                                                            title={text('msgLoadBalancerOfflinePhase')}
                                                            size={20}
                                                        />
                                                    </span>
                                                </div>

                                                <Controller
                                                    name={`lbOfflinePhase_${connector.connectorId}`}
                                                    control={control}
                                                    render={({ field: { value, onChange } }) => {
                                                        return (
                                                            <Dropdown
                                                                id={`lbOfflinePhase_${connector.connectorId}`}
                                                                options={singlePhaseOptions}
                                                                showClear={false}
                                                                disabled={
                                                                    chargePoint.inActiveLoadBalancerGroup ||
                                                                    isRestrictedAccess ||
                                                                    autoRefresh ||
                                                                    isLoadbalancerGroupActive
                                                                }
                                                                value={value}
                                                                onChange={(option) => {
                                                                    onChange(option.value)
                                                                }}
                                                            />
                                                        )
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </React.Fragment>
                                ))}
                            </Panel>
                        )}
                    </div>
                    <Panel
                        data-testid="capi-settings-form-container"
                        className="lg:col-6 col-12"
                        toggleable
                        header={
                            <span className="mt-2 flex">
                                <i className="pi pi-cog"></i>
                                <span className="ml-2"> {text('lblConnectorConfiguration')}</span>
                            </span>
                        }
                    >
                        <div className="ml-3 col-10">
                            {[...chargePoint.connectors]
                                .sort((a, b) => a.connectorId - b.connectorId)
                                .map((connector) => (
                                    <SConnector key={connector.connectorId}>
                                        <SConnectorTitle data-testid={'socket-info-field-' + connector.connectorId}>
                                            <Text
                                                code="lblSocketInfo"
                                                values={{
                                                    name: adaptConnectorNaming(connector.name),
                                                }}
                                            />

                                            <InfoIcon title={getInfo(connector)} size={18} />
                                        </SConnectorTitle>
                                        {connector.connectorFunctionType === 'Charger' && (
                                            <>
                                                <div className="mt-1">
                                                    <div data-testid="charger-info-data" className="mb-2">
                                                        <Text code="lblMaxPower" />
                                                    </div>
                                                    <Input
                                                        id={connector.connectorId}
                                                        type="number"
                                                        data-testid={`max-power-value-${connector.connectorId}`}
                                                        value={connector.factoryMaxPower}
                                                        disabled
                                                    />
                                                </div>
                                                <SFormRow>
                                                    <SLabel
                                                        htmlFor={`connectorType_${connector.connectorId}`}
                                                        data-testid={'type-field-' + connector.connectorId}
                                                    >
                                                        <Text code="lblSocketType" />
                                                    </SLabel>

                                                    <Controller
                                                        name={`connectorType_${connector.connectorId}`}
                                                        control={control}
                                                        render={({ field: { value, onChange } }) => {
                                                            return (
                                                                <Dropdown
                                                                    id={`connectorType_${connector.connectorId}`}
                                                                    options={connectorTypesOptions}
                                                                    value={value}
                                                                    onChange={(option) => {
                                                                        onChange(option.value)
                                                                    }}
                                                                    disabled={shouldDisabledInputs}
                                                                />
                                                            )
                                                        }}
                                                    />
                                                </SFormRow>
                                                <SFormRow>
                                                    <SLabel
                                                        htmlFor={`installationPhaseMode_${connector.connectorId}`}
                                                        data-testid={
                                                            'installationPhaseMode-field-' + connector.connectorId
                                                        }
                                                    >
                                                        <Text code="lblInstallationPhaseMode" />

                                                        <InfoIcon title={text('msgInstallationPhaseMode')} size={18} />
                                                    </SLabel>
                                                    <Controller
                                                        name={`installationPhaseMode_${connector.connectorId}`}
                                                        control={control}
                                                        render={({ field: { value, onChange } }) => {
                                                            return (
                                                                <Dropdown
                                                                    id={`installationPhaseMode_${connector.connectorId}`}
                                                                    options={capiPhaseModeOptions}
                                                                    value={value}
                                                                    onChange={(option) => {
                                                                        onChange(option.value)
                                                                    }}
                                                                    disabled={
                                                                        connector.factoryPhaseMode === '1P' ||
                                                                        chargePoint.inActiveLoadBalancerGroup ||
                                                                        isRestrictedAccess ||
                                                                        autoRefresh
                                                                    }
                                                                />
                                                            )
                                                        }}
                                                    />
                                                </SFormRow>
                                                <SFormRow>
                                                    <SLabel
                                                        htmlFor={`installationPhase_${connector.connectorId}`}
                                                        data-testid={'installationPhase-field-' + connector.connectorId}
                                                    >
                                                        <Text code="lblInstallationPhase" />

                                                        <InfoIcon title={text('msgCapiInstallationPhase')} size={18} />
                                                    </SLabel>

                                                    <Controller
                                                        name={`installationPhase_${connector.connectorId}`}
                                                        control={control}
                                                        render={({ field: { value, onChange } }) => {
                                                            return (
                                                                <Dropdown
                                                                    id={`installationPhase_${connector.connectorId}`}
                                                                    options={singlePhaseOptions}
                                                                    disabled={
                                                                        chargePoint.inActiveLoadBalancerGroup ||
                                                                        isRestrictedAccess ||
                                                                        autoRefresh
                                                                    }
                                                                    value={value}
                                                                    onChange={(option) => {
                                                                        onChange(option.value)
                                                                    }}
                                                                />
                                                            )
                                                        }}
                                                    />
                                                </SFormRow>
                                                <SFormRow>
                                                    <SLabel
                                                        data-testid={
                                                            'installationCurrent-field-' + connector.connectorId
                                                        }
                                                    >
                                                        <Text code="lblInstallationCurrentLimitField" />

                                                        <InfoIcon
                                                            title={text('msgCapiInstallationCurrentLimitField', {
                                                                factoryCurrent: connector.factoryCurrent,
                                                                supplyCurrent: chargePoint.supplyCurrent,
                                                            })}
                                                            size={18}
                                                        />
                                                    </SLabel>
                                                    <Input
                                                        type="number"
                                                        min={connector.minCurrent ?? CHARGEPOINT_MIN_CURRENT}
                                                        id={`ic_${connector.connectorId}`}
                                                        disabled={
                                                            chargePoint.inActiveLoadBalancerGroup ||
                                                            isRestrictedAccess ||
                                                            autoRefresh
                                                        }
                                                        data-testid={
                                                            'installationCurrent-input-' + connector.connectorId
                                                        }
                                                        error={errors[`ic_${connector.connectorId}`]?.message}
                                                        {...register(`ic_${connector.connectorId}`)}
                                                    />
                                                </SFormRow>
                                                <SFormRow>
                                                    <SLabel data-testid={'userCurrent-field-' + connector.connectorId}>
                                                        <Text code="lblUserCurrentLimitField" />

                                                        <InfoIcon title={text('msgUserCurrentLimitField')} size={18} />
                                                    </SLabel>
                                                    <Input
                                                        type="number"
                                                        min={connector.minCurrent ?? CHARGEPOINT_MIN_CURRENT}
                                                        id={`uc_${connector.connectorId}`}
                                                        disabled={shouldDisabledInputs}
                                                        data-testid={'userCurrent-input-' + connector.connectorId}
                                                        error={errors[`uc_${connector.connectorId}`]?.message}
                                                        {...register(`uc_${connector.connectorId}`)}
                                                    />
                                                </SFormRow>
                                            </>
                                        )}
                                        {connector.connectorFunctionType === 'Schuko' && (
                                            <div data-testid={'schuko-field-' + connector.connectorId}>
                                                <Text code="lblSocketConfigurationIsNotRequired" />
                                            </div>
                                        )}
                                    </SConnector>
                                ))}
                        </div>
                    </Panel>
                </div>
            </FormProvider>
        </div>
    )
}
